import React, { useState, useEffect } from "react";
import { Button } from "../ui/Button";
import { type Operator } from "../../services/StorageService";
import apiService from "../../services/ApiService";
import { NetworkError, ApiError } from "../../types/api";

export const VerificatorActivity: React.FC = () => {
  const [operators, setOperators] = useState<Operator[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const loadOperatorActivityFromApi = async () => {
    setIsLoading(true);
    setError("");

    try {
      if (!apiService.isAuthenticated()) {
        setError("Authentication required. Please log in again.");
        return;
      }

      // Fetch active shifts/sessions from API
      const shifts = await apiService.getShifts({
        status: "active",
      });

      // Transform shifts to operator format
      const operatorActivity: Operator[] = shifts.map((shift) => ({
        name: shift.pic_name || "Unknown",
        lastActive: shift.start_time,
        sessions: [
          {
            login: shift.start_time,
            logout: shift.end_time || null,
          },
        ],
      }));

      setOperators(operatorActivity);
    } catch (error) {
      if (error instanceof NetworkError) {
        setError("Network error. Please check your connection and try again.");
      } else if (error instanceof ApiError) {
        setError(`API Error: ${error.message}`);
      } else {
        setError("An unexpected error occurred");
        console.error("Load operator activity error:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const refreshData = () => {
    loadOperatorActivityFromApi();
  };

  useEffect(() => {
    // Load from API on mount
    loadOperatorActivityFromApi();
  }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">
          Verificator Activity
        </h3>

        {/* Status and controls */}
        <div className="flex items-center gap-3 text-sm">
          {isLoading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          )}

          <Button
            variant="secondary"
            size="sm"
            onClick={refreshData}
            disabled={isLoading || !apiService.isAuthenticated()}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="text-sm text-yellow-800">{error}</div>
        </div>
      )}

      {operators.length > 0 ? (
        <div className="space-y-4">
          {operators.map((operator) => {
            // Count total sessions
            const totalSessions = operator.sessions.length;

            // Get the most recent session
            const lastSession = operator.sessions[operator.sessions.length - 1];

            // Check if currently active
            const isCurrentlyActive = operator.sessions.some(
              (session) => session.logout === null
            );

            return (
              <div key={operator.name} className="border rounded p-3">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-medium">{operator.name}</h4>
                  {isCurrentlyActive && (
                    <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                      Active Session
                    </span>
                  )}
                </div>

                <p className="text-sm text-gray-600">
                  Last login: {new Date(lastSession.login).toLocaleString()}
                </p>

                {lastSession.logout && (
                  <p className="text-sm text-gray-600">
                    Last logout: {new Date(lastSession.logout).toLocaleString()}
                  </p>
                )}

                <p className="text-sm text-gray-600 mt-1">
                  Total sessions: {totalSessions}
                </p>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500 mb-4">No verificator activity recorded</p>

          {apiService.isAuthenticated() && (
            <Button
              variant="primary"
              onClick={refreshData}
              disabled={isLoading}
            >
              {isLoading ? "Loading..." : "Load from Server"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
